import { isNotNullish } from '@nx/stdlib';
import type { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query';

// oxlint-disable-next-line import/no-cycle
import consoleApi from '../console.api';
import {
  type CreatedDatabaseResponse,
  type DatabaseParam,
  type DatabaseSummary,
  type GetDatabaseResponse,
  isGetDatabaseResponse,
  isListDatabasesResponse,
} from '../console.api.types';
import type { AuraDatabase, AuraDatabaseSummary, CreatedDatabase } from '../console.types';
import {
  transformCreatedDatabaseResponse,
  transformDatabaseResponse,
  transformListDatabasesReponse,
} from '../transformers/databases';
import { type Builder, LIST_ID } from './types';

export const databaseEndpoints = (builder: Builder) => ({
  listDatabases: builder.query<AuraDatabase[], string>({
    queryFn: async (
      instanceId,
      _api,
      _extraOptions,
      baseQuery,
    ): Promise<QueryReturnValue<AuraDatabase[], FetchBaseQueryError, FetchBaseQueryMeta>> => {
      const listResponse = await baseQuery({
        url: `aura-instances/${instanceId}/aura-databases`,
        method: 'GET',
      });

      if (isNotNullish(listResponse.error)) {
        return { error: listResponse.error };
      }
      if (!isListDatabasesResponse(listResponse.data)) {
        return { error: { status: 'CUSTOM_ERROR', error: 'No valid database summary data received' } };
      }
      const databaseSummaries = listResponse.data.data;

      const fetchDetails = async (databaseSummary: DatabaseSummary): Promise<AuraDatabase> => {
        const databaseResponse = await baseQuery({
          url: `aura-instances/${instanceId}/aura-databases/${databaseSummary.aura_database_id}`,
          method: 'GET',
        });

        if (isNotNullish(databaseResponse.error)) {
          throw databaseResponse.error;
        }
        if (isGetDatabaseResponse(databaseResponse.data)) {
          return transformDatabaseResponse(databaseResponse.data);
        }

        throw new Error('No valid database details received');
      };

      try {
        const databases: AuraDatabase[] = await Promise.all(databaseSummaries.map(fetchDetails));
        return { data: databases };
      } catch (error: unknown) {
        return { error: { status: 'CUSTOM_ERROR', error: error instanceof Error ? error.message : String(error) } };
      }
    },
    providesTags: (result: AuraDatabase[] | undefined) => {
      if (isNotNullish(result)) {
        return [...result.map(({ id }) => ({ type: 'Database' as const, id })), { type: 'Database', id: LIST_ID }];
      }
      return [{ type: 'Database', id: LIST_ID }];
    },
  }),
  listDatabaseSummaries: builder.query<AuraDatabaseSummary[], string>({
    query: (instanceId) => `aura-instances/${instanceId}/aura-databases`,
    transformResponse: transformListDatabasesReponse,
    providesTags: (result) => {
      if (isNotNullish(result)) {
        return [...result.map(({ id }) => ({ type: 'Database' as const, id })), { type: 'Database', id: LIST_ID }];
      }
      return [{ type: 'Database', id: LIST_ID }];
    },
  }),
  getDatabase: builder.query<AuraDatabase, { instanceId: string; id: string }>({
    query: ({ instanceId, id }) => `aura-instances/${instanceId}/aura-databases/${id}`,
    transformResponse: (response: GetDatabaseResponse) => {
      return transformDatabaseResponse(response);
    },
    providesTags: (_result, _error, { id }) => [{ type: 'Database', id }],
  }),
  addDatabase: builder.mutation<CreatedDatabase, string>({
    query: (instanceId) => ({ method: 'POST', url: `aura-instances/${instanceId}/aura-databases` }),
    transformResponse: (response: CreatedDatabaseResponse) => {
      return transformCreatedDatabaseResponse(response);
    },
    invalidatesTags: (result: CreatedDatabase | undefined) => [
      { type: 'Database', id: result?.id },
      { type: 'Database', id: LIST_ID },
    ],
  }),
  deleteDatabase: builder.mutation<void, DatabaseParam>({
    query: ({ instanceId, id }) => ({
      method: 'DELETE',
      url: `aura-instances/${instanceId}/aura-databases/${id}`,
    }),
    onQueryStarted: ({ instanceId, id }, { getState, dispatch, queryFulfilled }) => {
      const state = getState();
      const cacheUpdates = consoleApi.util.selectCachedArgsForQuery(state, 'listDatabases').map((_) =>
        dispatch(
          consoleApi.util.updateQueryData('listDatabases', instanceId, (draft: AuraDatabase[]) => {
            const index = draft.findIndex((db) => db.id === id);
            if (index !== -1) {
              draft.splice(index, 1);
            }
          }),
        ),
      );
      queryFulfilled.catch(() => {
        cacheUpdates.forEach((update) => update.undo());
      });
    },
    invalidatesTags: (_result, _error, { id }) => [{ type: 'Database', id }],
  }),
});
